import React, { ReactNode, useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { MinimalAppSkeleton } from '@/components/ui/app-skeleton';
import { createScopedLogger } from '@/utils/debug';

interface AuthGuardProps {
  children?: ReactNode;
  requireCustomer?: boolean;
}

export const AuthGuard = ({ 
  children, 
  requireCustomer = true 
}: AuthGuardProps) => {
  const { 
    isAuthenticated, 
    isInitialized, 
    isLoading,
    activeCustomer,
    user,
    customers,
    selectCustomer
  } = useAuth();
  
  const location = useLocation();
  const [waitingForCustomer, setWaitingForCustomer] = useState(false);
  const [customerWaitTimeout, setCustomerWaitTimeout] = useState<NodeJS.Timeout | null>(null);
  const [attemptedAutoSelect, setAttemptedAutoSelect] = useState(false);

  // Create a logger function that returns the scoped logger object
  const logger = useCallback(() => createScopedLogger(user, 'AuthGuard'), [user]);
  
  // Helper function to log debug messages using the enhanced logger
  const logDebug = useCallback((message: string, data?: any) => {
    logger().debug(message, data);
  }, [logger]);

  // Use effect to track if we're in the process of auto-selecting a customer
  useEffect(() => {
    // If we're authenticated but don't have an activeCustomer yet
    if (isAuthenticated && !activeCustomer && requireCustomer && !waitingForCustomer && !attemptedAutoSelect) {
      // Check if we have a last active customer and customers data
      const hasLastActiveCustomer = !!user?.last_active_customer;
      const hasCustomers = customers.length > 0;
      
      logDebug('Checking customer status', {
        hasLastActiveCustomer,
        customerCount: customers.length,
        lastActiveId: user?.last_active_customer
      });
      
      // If we have what we need to potentially auto-select a customer, 
      // actively try to select it first
      if (hasLastActiveCustomer && hasCustomers && selectCustomer) {
        logDebug('User has last_active_customer, attempting to auto-select it');
        setWaitingForCustomer(true);
        
        // Try to find and select the last active customer
        const lastActiveCustomer = customers.find(c => c.id === user.last_active_customer);
        if (lastActiveCustomer) {
          logDebug(`Found last active customer (${lastActiveCustomer.name}), selecting it`);
          selectCustomer(lastActiveCustomer)
            .then(result => {
              if (result.success) {
                logDebug('Customer auto-selection successful');
                // Auto selection successful
              } else {
                logDebug('Customer auto-selection failed:', result.error);
              }
            })
            .catch(err => {
              console.error('Error during customer auto-selection:', err);
            })
            .finally(() => {
              setAttemptedAutoSelect(true);
            });
        } else {
          logDebug('Last active customer not found in available customers');
          setAttemptedAutoSelect(true);
        }
        
        // In any case, wait a bit longer before potentially redirecting to select-customer
        const timeout = setTimeout(() => {
          logDebug('Wait time for customer selection expired');
          setWaitingForCustomer(false);
        }, 2500); // Increased wait time to 2.5 seconds
        
        setCustomerWaitTimeout(timeout);
        return () => {
          if (customerWaitTimeout) {
            clearTimeout(customerWaitTimeout);
          }
        };
      }
    }
    
    // If we got an activeCustomer while waiting, clear the timeout
    if (activeCustomer && waitingForCustomer) {
      logDebug('activeCustomer set during wait period, clearing timeout');
      setWaitingForCustomer(false);
      if (customerWaitTimeout) {
        clearTimeout(customerWaitTimeout);
        setCustomerWaitTimeout(null);
      }
    }
  }, [isAuthenticated, activeCustomer, user, customers, requireCustomer, waitingForCustomer, customerWaitTimeout, selectCustomer, attemptedAutoSelect, logDebug]);

  // If auth is still initializing or we're waiting for customer selection, show skeleton loading
  if (!isInitialized || isLoading || waitingForCustomer) {
    return <MinimalAppSkeleton />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    logDebug('User not authenticated, redirecting to login');
    return (
      <Navigate 
        to="/login" 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // If auth requires customer selection and no customer is active
  if (requireCustomer && !activeCustomer) {
    logDebug('Customer required but no active customer, redirecting to select-customer');
    return (
      <Navigate 
        to="/select-customer" 
        state={{ from: location.pathname }}
        replace 
      />
    );
  }

  // Render children if provided, otherwise render the Outlet for nested routes
  return <>{children || <Outlet />}</>;
};
